import { inject, Injectable, signal } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { environment } from 'src/environments/environment';
import { AuthService } from '@/core/services/auth.service';
import { HelpersService } from '@/shared/services/helpers.service';
import { NotificationItem, NotificationPriority } from './data-access/notifications.model';
import { NotificationsService } from './notifications.service';

const EVENT_NAMES = ['notification.received'] as const;

const ENABLED_PREF_KEY = 'betta.desktopNotifications.enabled';

type PopupContent = Pick<NotificationItem, 'id' | 'title' | 'message' | 'priority'>;

@Injectable({ providedIn: 'root' })
export class NotificationsLiveService {
    private readonly _authService = inject(AuthService);
    private readonly _helpersService = inject(HelpersService);
    private readonly _notificationsService = inject(NotificationsService);

    private _connection: HubConnection | null = null;

    /** Keyed on timestamp, not id: the backend reuses ids across distinct notifications. */
    private _lastNotifiedAt: string | null = null;

    /** Keeps every native pop-up's tag unique, because a repeated tag makes the OS silently
     *  replace the previous banner instead of showing a new one, and backend ids repeat. */
    private _popupSeq = 0;

    public readonly connectionState = signal<HubConnectionState>(HubConnectionState.Disconnected);

    public readonly permission = signal<NotificationPermission | 'unsupported'>(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');

    public readonly enabled = signal<boolean>(this.readEnabledPref());

    constructor() {
        this.watchPermissionChanges();
    }

    /** Notification.permission is snapshotted once and goes stale when the user changes it in
     *  site settings, so the live state comes from the Permissions API with a focus fallback. */
    private watchPermissionChanges(): void {
        if (typeof Notification === 'undefined') {
            return;
        }

        if (typeof navigator !== 'undefined' && navigator.permissions?.query) {
            navigator.permissions
                .query({ name: 'notifications' as PermissionName })
                .then((status) => {
                    const apply = () => this.setPermission(this.mapPermissionState(status.state));
                    apply();
                    status.onchange = apply;
                })
                .catch(() => {});
        }

        if (typeof window !== 'undefined') {
            window.addEventListener('focus', () => this.syncPermission());
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible') {
                    this.syncPermission();
                }
            });
        }
    }

    private syncPermission(): void {
        if (typeof Notification !== 'undefined') {
            this.setPermission(Notification.permission);
        }
    }

    /** Reports a browser-side revoke (granted -> denied/default), which otherwise happens silently. */
    private setPermission(next: NotificationPermission): void {
        const previous = this.permission();
        this.permission.set(next);

        if (previous === 'granted' && next !== 'granted' && this.enabled()) {
            this._helpersService.showWarningMessage('Your browser turned off desktop notifications for this site. New notifications will appear here in the app instead.');
        }
    }

    private mapPermissionState(state: PermissionState): NotificationPermission {
        if (state === 'granted') {
            return 'granted';
        }
        if (state === 'denied') {
            return 'denied';
        }
        return 'default';
    }

    public async connect(): Promise<void> {
        if (this._connection && this._connection.state !== HubConnectionState.Disconnected) {
            return;
        }
        if (!this._authService.getAccessToken()) {
            return;
        }

        this._connection = new HubConnectionBuilder()
            .withUrl(environment.urls.hubs.notifications, {
                accessTokenFactory: () => this._authService.getAccessToken() ?? ''
            })
            .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
            .configureLogging(LogLevel.Warning)
            .build();

        this._connection.onreconnecting(() => this.connectionState.set(HubConnectionState.Reconnecting));
        this._connection.onreconnected(() => this.connectionState.set(HubConnectionState.Connected));
        this._connection.onclose(() => this.connectionState.set(HubConnectionState.Disconnected));

        for (const eventName of EVENT_NAMES) {
            this._connection.on(eventName, (payload: NotificationItem) => this.handleEvent(payload));
        }

        try {
            this.connectionState.set(HubConnectionState.Connecting);
            await this._connection.start();
            this.connectionState.set(HubConnectionState.Connected);
            this.seedLastNotifiedId();
        } catch {
            this.connectionState.set(HubConnectionState.Disconnected);
        }
    }

    private seedLastNotifiedId(): void {
        this._notificationsService.getNotificationsSummary$().subscribe((summary) => {
            this._lastNotifiedAt = summary.recentNotifications?.[0]?.createdAt ?? summary.lastNotificationDate ?? null;
        });
    }

    public async disconnect(): Promise<void> {
        if (!this._connection) {
            return;
        }
        try {
            await this._connection.stop();
        } finally {
            this._connection = null;
            this.connectionState.set(HubConnectionState.Disconnected);
        }
    }

    /** The hub often pushes a null payload, so the summary re-fetch is both the badge refresh
     *  and the only way to know what to pop. Payload-bearing events are not deduped by id. */
    private handleEvent(payload: NotificationItem | null): void {
        if (payload?.id != null) {
            this._notificationsService.prependLiveNotification(payload);
            this.showPopup(payload);
        }

        this._notificationsService.getNotificationsSummary$().subscribe((summary) => {
            if (payload?.id != null) {
                return;
            }
            const latest = summary.recentNotifications?.[0] ?? null;
            if (latest && latest.createdAt !== this._lastNotifiedAt) {
                this._lastNotifiedAt = latest.createdAt;
                this.showPopup(latest);
            }
        });
    }

    /** No-ops unless the permission is still 'default', so it is safe to call from anywhere,
     *  but it only prompts reliably when reached from a user gesture. */
    public requestBrowserPermission(): void {
        if (typeof Notification === 'undefined' || Notification.permission !== 'default') {
            return;
        }
        Notification.requestPermission()
            .then((result) => this.setPermission(result))
            .catch(() => {});
    }

    public async sendTestNotification(): Promise<void> {
        // Awaited, unlike requestBrowserPermission(), so the pop-up reflects the choice just made.
        if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
            try {
                this.setPermission(await Notification.requestPermission());
            } catch {}
        }

        this.showPopup({
            id: 0,
            title: 'Test notification',
            message: 'Desktop notifications are working. This is how a new notification will reach you.',
            priority: NotificationPriority.LOW
        });
    }

    private readEnabledPref(): boolean {
        try {
            const stored = localStorage.getItem(ENABLED_PREF_KEY);
            return stored === null ? true : stored === 'true';
        } catch {
            return true;
        }
    }

    public setEnabled(enabled: boolean): void {
        this.enabled.set(enabled);
        try {
            localStorage.setItem(ENABLED_PREF_KEY, String(enabled));
        } catch {}
        if (enabled) {
            this.requestBrowserPermission();
        }
    }

    private showPopup(item: PopupContent): void {
        const message = item.title ?? item.message ?? '';
        if (!message) {
            return;
        }

        if (this.enabled() && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            try {
                const nativeNotification = new Notification(item.title ?? 'BettaAlpha', {
                    body: item.message ?? '',
                    tag: `betta-notification-${item.id}-${++this._popupSeq}`
                });
                nativeNotification.onclick = () => {
                    window.focus();
                    nativeNotification.close();
                };
                return;
            } catch {}
        }

        this.showInAppToast(item, message);
    }

    private showInAppToast(item: PopupContent, message: string): void {
        switch (item.priority) {
            case NotificationPriority.CRITICAL:
                this._helpersService.showErrorMessage(message);
                break;
            case NotificationPriority.HIGH:
                this._helpersService.showWarningMessage(message);
                break;
            default:
                this._helpersService.showInfoMessage(message);
                break;
        }
    }
}
